/**
 * SocialIcons — shared component used by the GetVerifiedPage modal,
 * the ExpertProfilePage, and the ExpertsPage browse cards.
 *
 * Renders a row of branded circular icon buttons for LinkedIn, GitHub,
 * and X (Twitter). Each icon goes dim + disabled when its URL is absent
 * so the row is visually stable across experts (a lawyer with no GitHub
 * still shows the GitHub slot, just dim — cards stay comparable).
 *
 * The brand glyphs are inlined SVG paths so we get the official marks
 * (not lucide's generic outline icons). About 30 LOC of glyph data total.
 *
 * Defensive URL handling: if the seed-data row has a bare handle
 * ("ankit3563", "@ankit3563") rather than a full URL, we wrap it with
 * the right base so the click lands somewhere useful.
 */

import React from "react";

const BRAND = {
  linkedin: "#0A66C2",
  github:   "#1F2328",
  x:        "#0F1419",
};

const Glyph = {
  linkedin: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden>
      <path d="M20.45 20.45h-3.55V14.9c0-1.33-.02-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.65H9.36V9h3.4v1.56h.05c.47-.89 1.62-1.83 3.33-1.83 3.56 0 4.22 2.34 4.22 5.39v6.33ZM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12Zm1.78 13.02H3.56V9h3.56v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0Z"/>
    </svg>
  ),
  github: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden>
      <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.11.79-.25.79-.55 0-.27-.01-1.16-.02-2.1-3.2.69-3.87-1.36-3.87-1.36-.52-1.32-1.28-1.68-1.28-1.68-1.05-.72.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.76 2.71 1.25 3.37.96.1-.75.4-1.25.73-1.54-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.28 1.18-3.09-.12-.29-.51-1.46.11-3.04 0 0 .97-.31 3.18 1.18a11 11 0 0 1 5.79 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.58.24 2.75.12 3.04.74.81 1.18 1.83 1.18 3.09 0 4.42-2.7 5.4-5.27 5.68.41.36.78 1.07.78 2.16 0 1.56-.01 2.81-.01 3.19 0 .31.2.67.79.55C20.21 21.39 23.5 17.07 23.5 12 23.5 5.65 18.35.5 12 .5Z"/>
    </svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817-5.967 6.817H1.677l7.73-8.835L1.255 2.25h6.83l4.713 6.231 5.446-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z"/>
    </svg>
  ),
};


// One circular icon button. Disabled / dim if href is empty.
export function SocialIconLink({ href, label, brand, size = "md", children }) {
  const enabled = !!href;
  const dim = "w-9 h-9";
  const sm  = "w-7 h-7";
  const sizeCls = size === "sm" ? sm : dim;

  if (!enabled) {
    return (
      <span
        title={`${label} not provided`}
        aria-label={`${label} not provided`}
        className={[
          sizeCls,
          "rounded-full border flex items-center justify-center",
          "border-slateline-200 bg-slateline-50 text-slateline-300 cursor-not-allowed",
        ].join(" ")}
      >
        {children}
      </span>
    );
  }

  // Bare-handle fallback: wrap "ankit3563" → full URL based on brand.
  const v = String(href).trim();
  const normalized = /^https?:\/\//i.test(v)
    ? v
    : brand === "linkedin" ? `https://www.linkedin.com/in/${v.replace(/^@/, "")}`
    : brand === "github"   ? `https://github.com/${v.replace(/^@/, "")}`
    : brand === "x"        ? `https://x.com/${v.replace(/^@/, "")}`
                           : `https://${v}`;

  return (
    <a
      href={normalized}
      target="_blank"
      rel="noopener noreferrer"
      title={label}
      aria-label={`Open ${label} profile in a new tab`}
      className={[
        sizeCls,
        "rounded-full border flex items-center justify-center transition-all",
        "border-slateline-200 bg-canvas-card text-slateline-500",
        "hover:text-white hover:border-transparent",
      ].join(" ")}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = BRAND[brand]; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ""; }}
    >
      {children}
    </a>
  );
}


/**
 * SocialIconRow — convenience wrapper. Pass the expert object; we read
 * the three URL fields (with fallbacks for legacy column names) and
 * render the row. Returns null if the expert is stay_anonymous so the
 * caller doesn't need to guard.
 */
export default function SocialIconRow({ expert, size = "md", className = "" }) {
  if (!expert || expert.stay_anonymous) return null;

  const linkedin = expert.linkedin_url || expert.linkedin || "";
  const github   = expert.github_url   || expert.github   || "";
  const x        = expert.twitter_url  || expert.twitter  || expert.x_url || expert.x || "";

  return (
    <div className={`flex gap-1.5 ${className}`} aria-label="Expert social links">
      <SocialIconLink href={linkedin} label="LinkedIn" brand="linkedin" size={size}>
        {Glyph.linkedin}
      </SocialIconLink>
      <SocialIconLink href={github} label="GitHub" brand="github" size={size}>
        {Glyph.github}
      </SocialIconLink>
      <SocialIconLink href={x} label="X / Twitter" brand="x" size={size}>
        {Glyph.x}
      </SocialIconLink>
    </div>
  );
}
